import { FileWarning, UserPlus, Users as UsersIcon } from 'lucide-react';
import { useState, type ReactElement, type ReactNode } from 'react';

import { GatedChrome, RestrictedPanel } from '@/components/auth/GatedChrome';
import { PermissionErrorPanel } from '@/components/auth/PermissionErrorPanel';
import { ExportMenu } from '@/components/ui/ExportMenu';
import { InlineError, StatePanel } from '@/components/ui/StatePanel';
import { usePermission, usePermissionState } from '@/hooks/usePermission';
import { useMe } from '@/lib/me';

import { AddUserModal } from './components/AddUserModal';
import { UserDirectory } from './components/UserDirectory';
import { UsersFilterBar } from './components/UsersFilterBar';
import { UsersSkeleton } from './components/UsersSkeleton';
import { toUsersQuery, useUsersFilters } from './use-url-filters';
import { useRoles, usersExportSearch, useUsersDirectory } from './use-users';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

const BREADCRUMB = [
  { label: 'QMS', to: '/dashboard', strong: true },
  { label: 'Configuración' },
  { label: 'Usuarios', strong: true },
];

function Frame({ children }: { children: ReactNode }): ReactElement {
  // G4: sin permiso confirmado, las migas y el encabezado no nombran la pantalla.
  const perm = usePermissionState('system:users:manage');
  return (
    <>
      <GatedChrome state={perm.state} breadcrumb={BREADCRUMB} navContext="Usuarios" />
      {children}
    </>
  );
}

/** Configuración › Usuarios: quién entra al QMS, de qué empresas es (fuente: LMS),
 *  con qué roles, y su estado (activo, pendiente, desactivado). */
export function Users(): ReactElement {
  const perm = usePermissionState('system:users:manage');
  const canManageRoles = usePermission('system:roles:manage');
  const me = useMe();
  const { filters, setQuery, setSegment, clear } = useUsersFilters();
  const granted = perm.state === 'granted';
  const directory = useUsersDirectory(toUsersQuery(filters), { enabled: granted });
  const roles = useRoles({ enabled: granted && canManageRoles });
  // Sin roles no se puede asignar ninguno: se avisa en el directorio y en el modal (UX-ERR-01).
  // Si falla una recarga con roles ya cargados, se siguen usando y solo se avisa en el directorio.
  const retryRoles = {
    onRetry: () => void roles.refetch(),
    retrying: roles.isFetching,
    cause: roles.error,
  };
  const rolesError = roles.isError && roles.data === undefined ? retryRoles : null;
  const rolesStale = roles.isError && roles.data !== undefined;
  const [addOpen, setAddOpen] = useState(false);

  if (perm.state === 'error') {
    return (
      <Frame>
        <PermissionErrorPanel error={perm.error} onRetry={perm.retry} retrying={perm.retrying} />
      </Frame>
    );
  }
  if (perm.state === 'denied') {
    return (
      <Frame>
        <RestrictedPanel />
      </Frame>
    );
  }

  const filtered = filters.q.trim() !== '' || filters.segment !== 'todos';
  const users = directory.data?.pages.flatMap((p) => p.data) ?? [];

  function body(): ReactElement {
    if (perm.state === 'loading' || directory.isLoading) {
      return <UsersSkeleton />;
    }
    if (directory.isError || !directory.data) {
      return (
        <StatePanel
          icon={<FileWarning />}
          title="No se pudieron cargar los usuarios"
          tone="danger"
          role="alert"
          onRetry={() => void directory.refetch()}
        >
          {errorAdvice(directory.error)}
        </StatePanel>
      );
    }
    if (users.length === 0) {
      return filtered ? (
        <StatePanel
          icon={<UsersIcon />}
          title="Ningún usuario coincide con la búsqueda"
          role="status"
        >
          Prueba con otro nombre o correo, o cambia el estado.{' '}
          <button type="button" className="btn sm mut" onClick={clear}>
            Limpiar filtros
          </button>
        </StatePanel>
      ) : (
        <StatePanel
          icon={<UsersIcon />}
          title="Todavía no hay usuarios en esta empresa"
          role="status"
        >
          Los colaboradores del LMS se agregan al ingresar por primera vez al QMS. Con Agregar
          usuario puedes asignarles un rol antes.
        </StatePanel>
      );
    }
    return (
      <div className="card rise">
        {(rolesError || rolesStale) && (
          <InlineError
            icon={<FileWarning />}
            onRetry={retryRoles.onRetry}
            retrying={retryRoles.retrying}
          >
            {rolesStale
              ? `No se pudieron actualizar los roles. Se muestran los anteriores.${refreshAdvice(roles.error)}`
              : `No se pudieron cargar los roles: por ahora no se pueden asignar. ${errorAdvice(roles.error)}`}
          </InlineError>
        )}
        <UserDirectory
          users={users}
          roles={roles.data ?? []}
          meId={me.data?.data.user_id ?? null}
          crossCompany={me.data?.data.cross_company ?? false}
          canManageRoles={canManageRoles}
        />
        {directory.hasNextPage && (
          <div className="cb" style={{ textAlign: 'center' }}>
            <button
              type="button"
              className="btn sm mut"
              disabled={directory.isFetchingNextPage}
              onClick={() => void directory.fetchNextPage()}
            >
              {directory.isFetchingNextPage ? 'Cargando…' : 'Cargar más usuarios'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <Frame>
      <div className="pagehead rise">
        <div className="grow">
          <h1 style={{ marginBottom: 4 }}>Usuarios</h1>
          <p style={{ maxWidth: '70ch' }}>
            Usuarios del QMS de la empresa activa, con sus roles y estado de acceso.
          </p>
        </div>
        {granted && (
          <div className="pagehead-actions">
            {/* Exportación (spec 2026-10-14 §3): lo filtrado completo, en CSV o PDF. */}
            <ExportMenu
              path="/api/v1/admin/users/export"
              query={usersExportSearch(toUsersQuery(filters))}
              title="Usuarios"
            />
            {canManageRoles && (
              <button type="button" className="btn" onClick={() => setAddOpen(true)}>
                <UserPlus aria-hidden="true" strokeWidth={1.9} />
                Agregar usuario
              </button>
            )}
          </div>
        )}
      </div>
      <UsersFilterBar filters={filters} onQuery={setQuery} onSegment={setSegment} />
      {body()}
      <AddUserModal
        open={addOpen}
        roles={roles.data ?? []}
        rolesError={rolesError}
        onClose={() => setAddOpen(false)}
      />
    </Frame>
  );
}
